// Capture a region of the live <video> element and produce a square PNG Blob.
//
// The Capture phase shows the video full-bleed behind a 9×16 grid and asks the
// user to align their face into a specific sub-region (area 2). We crop to
// that display-space rect, mapping back through the video's object-fit: cover
// transform to its source pixels.
//
// Mirror handling: the front-facing camera is shown mirrored (CSS scaleX(-1))
// for UX. The saved PNG is mirrored too — keeping viewfinder and captured
// frame consistent. Flip in the canvas, not in source coordinates, so the
// drawImage rect math stays simple.

import { UPLOAD_RESOLUTION } from "../config/capture";
import { computeCoverMap, displayRectToSource } from "./cover-map";

export type DisplayRect = { x: number; y: number; w: number; h: number };

export async function captureVideoRegionAsPng(
  video: HTMLVideoElement,
  region: DisplayRect,
  options: { mirror?: boolean } = {},
): Promise<Blob> {
  const mirror = options.mirror ?? true;

  // object-fit: cover transform (shared with framing.ts / the guide overlay).
  const map = computeCoverMap(video);
  if (!map) throw new Error("video has no frame or layout box yet");

  // Map the display-space region back to source-pixel coordinates.
  const { x: sx, y: sy, w: sw, h: sh } = displayRectToSource(map, region);

  const canvas = document.createElement("canvas");
  canvas.width = UPLOAD_RESOLUTION;
  canvas.height = UPLOAD_RESOLUTION;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("could not get 2d context");

  if (mirror) {
    ctx.translate(UPLOAD_RESOLUTION, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, UPLOAD_RESOLUTION, UPLOAD_RESOLUTION);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob returned null"))),
      "image/png",
    );
  });
}
